import { Action, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { FolderService, type WorkspaceType } from "@/services/FolderService";
import { fileActions } from "../file/fileSlice";
import { startLoading, stopLoading } from "../loading";
import type { RootEpic } from "../types";
import { concat, from, of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";
import { message } from "antd";
import { handleHttpError } from "@/api/HttpClient";
import filesService from "@/features/file-management/pages/FilePage/services/files.service";
import { mapApiToFolderRecord } from "@/features/file-management/pages/FilePage/utils/fileUtils";
import { Epic, ofType } from "redux-observable";

export type BreadcrumbItem = {
  id: string;
  name: string;
  isRoot?: boolean;
};

interface NavigationState {
  currentFolderId: string | null;
  breadcrumbs: BreadcrumbItem[];
  folderRootId: string;
  currentWorkspace: WorkspaceType | null;
  ownerRootId: string | null;
  loading: boolean;
  error: string | null;
  dataSingleFile?: any[];
}

// Helper functions for sessionStorage
const getSessionState = () => {
  try {
    const savedState = sessionStorage.getItem('navigationState');
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (error) {
    console.error('Failed to parse sessionStorage navigationState:', error);
  }
  return null;
};

const saveSessionState = (state: NavigationState) => {
  try {
    const stateToSave = {
      currentFolderId: state.currentFolderId,
      breadcrumbs: state.breadcrumbs,
      currentWorkspace: state.currentWorkspace,
      // folderRootId: state.folderRootId,
      ownerRootId: state.ownerRootId,
    };
    sessionStorage.setItem('navigationState', JSON.stringify(stateToSave));
  } catch (error) {
    console.error('Failed to save navigationState to sessionStorage:', error);
  }
};

const sessionState = getSessionState();

const initialState: NavigationState = {
  currentFolderId: sessionState?.currentFolderId || localStorage.getItem("folderId") || localStorage.getItem("folderRootId") || "",
  breadcrumbs: sessionState?.breadcrumbs || [],
  folderRootId:  localStorage.getItem("folderRootId") || "",
  currentWorkspace: sessionState?.currentWorkspace || null,
  ownerRootId: sessionState?.ownerRootId || null,
  loading: false,
  error: null,
};

const navigationSlice = createSlice({
  name: "navigation",
  initialState,
  reducers: {
    // Set folder root ID
    setFolderRootId: (state, action: PayloadAction<string>) => {
      state.folderRootId = action.payload;
      // Initialize breadcrumbs with root if empty
      if (state.breadcrumbs.length === 0) {
        state.breadcrumbs = [
          { id: action.payload, name: "Root", isRoot: true },
        ];
      }
      saveSessionState(state);
    },

    // Navigate to folder action (triggers epic)
    navigateToFolderRequest: (
      state,
      _action: PayloadAction<{
        folderId: string;
        folderName: string;
        page?: number;
        pageSize?: number;
        pageType?: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },

    // Navigate to folder success
    navigateToFolderSuccess: (
      state,
      action: PayloadAction<{
        folderId: string;
        folderName: string;
      }>
    ) => {
      const { folderId, folderName } = action.payload;
      state.currentFolderId = folderId;
      state.loading = false;

      // Add to breadcrumbs only if it's not already the last one
      const lastBreadcrumb = state.breadcrumbs[state.breadcrumbs.length - 1];
      if (lastBreadcrumb?.id !== folderId) {
        state.breadcrumbs = [
          ...state.breadcrumbs,
          { id: folderId, name: folderName },
        ];
      }
      saveSessionState(state);
    },

    // Navigate to folder failure
    navigateToFolderFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Navigate to breadcrumb action (triggers epic)
    navigateToBreadcrumbRequest: (
      state,
      _action: PayloadAction<{
        breadcrumbIndex: number;
        page?: number;
        pageSize?: number;
        pageType?: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },

    // Navigate to breadcrumb success
    navigateToBreadcrumbSuccess: (
      state,
      action: PayloadAction<{ breadcrumbIndex: number }>
    ) => {
      const { breadcrumbIndex } = action.payload;
      state.loading = false;

      if (breadcrumbIndex === -1) {
        // Navigate to root
        state.currentFolderId = state.folderRootId;
        state.breadcrumbs = [
          { id: state.folderRootId, name: "Root", isRoot: true },
        ];
      } else {
        // Navigate to specific breadcrumb
        const targetBreadcrumb = state.breadcrumbs[breadcrumbIndex];
        if (targetBreadcrumb) {
          state.currentFolderId = targetBreadcrumb.id;
          state.breadcrumbs = state.breadcrumbs.slice(0, breadcrumbIndex + 1);
        }
      }
      saveSessionState(state);
    },

    // Navigate to breadcrumb failure
    navigateToBreadcrumbFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    resetToRootRequest: (
      state,
      _action: PayloadAction<{
        pageType?: string; // "files" | "drawings" | etc
        page?: number;
        pageSize?: number;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },

    // Reset to root success
    resetToRootSuccess: (state) => {
      state.loading = false;
      state.currentFolderId = state.folderRootId;
      state.breadcrumbs = [
        { id: state.folderRootId, name: "Root", isRoot: true },
      ];
      saveSessionState(state);
    },

    // Set breadcrumbs manually (for initialization)
    setBreadcrumbs: (state, action: PayloadAction<BreadcrumbItem[]>) => {
      state.breadcrumbs = action.payload;
      saveSessionState(state);
    },

    // Set current folder ID
    setCurrentFolderId: (state, action: PayloadAction<string | null>) => {
      state.currentFolderId = action.payload;
      saveSessionState(state);
    },
    setDataSingleFile: (state, action: PayloadAction<any[]>) => {
      state.dataSingleFile = action.payload;
      state.loading = false;
    },

    // Update breadcrumb name when folder is renamed
    updateBreadcrumbName: (
      state,
      action: PayloadAction<{ folderId: string; newName: string }>
    ) => {
      const { folderId, newName } = action.payload;
      const index = state.breadcrumbs.findIndex((b) => b.id === folderId);
      if (index !== -1) {
        state.breadcrumbs[index].name = newName;
        saveSessionState(state);
      }
    },
    
    // Remove breadcrumb when folder is deleted and navigate to parent
    removeBreadcrumb: (state, action: PayloadAction<string>) => {
      const folderId = action.payload;
      const index = state.breadcrumbs.findIndex((b) => b.id === folderId);
      if (index !== -1) {
        // Remove the deleted folder and all children from breadcrumbs
        const newBreadcrumbs = state.breadcrumbs.slice(0, index);
        state.breadcrumbs = newBreadcrumbs;
        
        const lastBreadcrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
        state.currentFolderId = lastBreadcrumb?.id || state.folderRootId;
        saveSessionState(state);
      }
    },

    // Resolve from link (link sharing)
    resolveFromLinkRequest: (
      state,
      _action: PayloadAction<{
        folderId: string;
        page?: number;
        pageSize?: number;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },

    resolveFromLinkSuccess: (
      state,
      action: PayloadAction<{
        folderId: string;
        folderName: string;
        workspace: WorkspaceType;
        breadcrumbPath: BreadcrumbItem[];
        ownerRootId: string;
      }>
    ) => {
      const { folderId, workspace, breadcrumbPath, ownerRootId } = action.payload;
      state.loading = false;
      state.currentFolderId = folderId;
      state.currentWorkspace = workspace;
      state.breadcrumbs = breadcrumbPath;
      state.ownerRootId = ownerRootId;
      saveSessionState(state);
    },

    resolveFromLinkFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Resolve from file link (file link sharing)
    resolveFromFileLinkRequest: (
      state,
      _action: PayloadAction<{
        fileId: string;
        page?: number;
        pageSize?: number;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },

    resolveFromFileLinkSuccess: (
      state,
      action: PayloadAction<{
        parentFolderId: string;
        folderName: string;
        workspace: WorkspaceType;
        breadcrumbPath: BreadcrumbItem[];
        ownerRootId: string;
      }>
    ) => {
      const { parentFolderId, workspace, breadcrumbPath, ownerRootId } = action.payload;
      state.loading = false;
      state.currentFolderId = parentFolderId;
      state.currentWorkspace = workspace;
      state.breadcrumbs = breadcrumbPath;
      state.ownerRootId = ownerRootId;
      saveSessionState(state);
    },

    resolveFromFileLinkFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Set workspace
    setWorkspace: (state, action: PayloadAction<WorkspaceType | null>) => {
      state.currentWorkspace = action.payload;
      saveSessionState(state);
    },

    // Load workspace data (triggers epic)
    loadWorkspaceRequest: (
      state,
      _action: PayloadAction<{
        workspace: WorkspaceType;
        page?: number;
        pageSize?: number;
        folderRootId: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },

    loadWorkspaceSuccess: (
      state,
      action: PayloadAction<{
        workspace: WorkspaceType;
        rootFolderId: string;
      }>
    ) => {
      const { workspace, rootFolderId } = action.payload;
      state.loading = false;
      state.currentWorkspace = workspace;
      state.currentFolderId = rootFolderId;
      state.breadcrumbs = [
        { id: rootFolderId, name: workspace, isRoot: true },
      ];
      saveSessionState(state);
    },

    loadWorkspaceFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Listen for project slice folderRootId changes
    builder.addMatcher(
      (action): action is PayloadAction<any> =>
        action.type === "project/setFolderRootId" ||
        action.type === "project/getFolderRootIdSuccess",
      (state, action) => {
        const folderRootId = action.payload?.folderRootId || action.payload;
        if (folderRootId && typeof folderRootId === "string") {
          state.folderRootId = folderRootId;
          // Initialize breadcrumbs with root if empty
          if (state.breadcrumbs.length === 0) {
            state.breadcrumbs = [
              { id: folderRootId, name: "Root", isRoot: true },
            ];
          }
        }
      }
    );
  },
});

// Epic for navigating to folder
export const navigateToFolderEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(navigationActions.navigateToFolderRequest.match),
    switchMap((action) => {
      const {
        folderId,
        folderName,
        page = 1,
        pageSize = 20,
        pageType,
      } = action.payload;

      return concat(
        [startLoading({ key: "navigateToFolder" })],
        // Use different service based on page type
        (pageType === "teamDrives"
          ? FolderService.Get.getTeamFilesByFolderId(folderId, {
              page,
              pageSize,
              isOrganization: true,
            })
          : FolderService.Get.getFilesByFolderId(folderId, { page, pageSize })
        ).pipe(
          switchMap((res) => {
            // Store folder ID in localStorage for persistence
            localStorage.setItem("folderId", folderId);

            // Map response to file records
            const projectFiles = (res.data.results || []);

            return from([
              navigationActions.navigateToFolderSuccess({
                folderId,
                folderName,
              }),
              fileActions.setParentFiles(projectFiles),
              fileActions.setPagination({
                page: res.data.page,
                pageCount: res.data.pageCount,
                pageSize: res.data.pageSize,
                queryCount: res.data.queryCount,
                firstRowIndex: (res.data.page - 1) * res.data.pageSize + 1,
                lastRowIndex: Math.min(res.data.page * res.data.pageSize, res.data.queryCount),
              }),
            ]);
          }),
          catchError((err) => {
            message.error(handleHttpError(err));
            return of(
              navigationActions.navigateToFolderFailure(
                err.response.Message || "Error navigating to folder"
              )
            );
          })
        ),
        [stopLoading({ key: "navigateToFolder" })]
      );
    })
  );

// Epic for navigating to breadcrumb
export const navigateToBreadcrumbEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(navigationActions.navigateToBreadcrumbRequest.match),
    switchMap((action) => {
      const {
        breadcrumbIndex,
        page = 1,
        pageSize = 20,
        pageType,
      } = action.payload;
      const state = state$.value;
      const { breadcrumbs } = state.navigation;
      const folderRootId =
        localStorage.getItem("folderRootId") || state.navigation.folderRootId;

      // Determine target folder ID
      let targetFolderId: string;
      if (breadcrumbIndex === -1) {
        targetFolderId = folderRootId;
      } else {
        const targetBreadcrumb = breadcrumbs[breadcrumbIndex];
        if (!targetBreadcrumb) {
          return of(
            navigationActions.navigateToBreadcrumbFailure(
              "Invalid breadcrumb index"
            )
          );
        }
        targetFolderId = targetBreadcrumb.id;
      }

      return concat(
        [startLoading({ key: "navigateToBreadcrumb" })],
        // Use different service based on page type
        (pageType === "teamDrives"
          ? FolderService.Get.getTeamFilesByFolderId(targetFolderId, {
              page,
              pageSize,
              isOrganization: true,
            })
          : FolderService.Get.getFilesByFolderId(targetFolderId, {
              page,
              pageSize,
            })
        ).pipe(
          switchMap((res) => {
            // Store folder ID in localStorage for persistence
            localStorage.setItem("folderId", targetFolderId);

            // Map response to file records
            const projectFiles = (res.data.results || []);

            return from([
              navigationActions.navigateToBreadcrumbSuccess({
                breadcrumbIndex,
              }),
              fileActions.setParentFiles(projectFiles),
              fileActions.setPagination({
                page: res.data.page,
                pageCount: res.data.pageCount,
                pageSize: res.data.pageSize,
                queryCount: res.data.queryCount,
                firstRowIndex: (res.data.page - 1) * res.data.pageSize + 1,
                lastRowIndex: Math.min(res.data.page * res.data.pageSize, res.data.queryCount),
              }),
            ]);
          }),
          catchError((err) => {
            console.error("Error resetting to root:", err);
            message.error(handleHttpError(err));
            return of(
              navigationActions.navigateToBreadcrumbFailure(
                err.response.Message || "Error navigating to breadcrumb"
              )
            );
          })
        ),
        [stopLoading({ key: "navigateToBreadcrumb" })]
      );
    })
  );

export const resetToRootEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(navigationActions.resetToRootRequest.match),
    switchMap((action) => {
      const { pageType = "files", page = 1, pageSize = 20 } = action.payload;
      const state = state$.value;
      const { folderRootId } = state.navigation;
      //console.log("Resetting to root for pageType:", folderRootId);

      if (pageType === "files" && folderRootId) {
        return concat(
          [startLoading({ key: "resetToRoot" })],
          FolderService.Get.getFilesByFolderId(folderRootId, {
            page,
            pageSize,
          }).pipe(
            switchMap((res) => {
              localStorage.setItem("folderId", folderRootId);

              const projectFiles = (res.data.results || []);

              return from([
                navigationActions.resetToRootSuccess(),
              ]);
            }),
            catchError((err) => {
              console.error("Error resetting to root:", err);
              return of(
                navigationActions.navigateToFolderFailure(
                  err.response.Message || "Error resetting to root"
                )
              );
            })
          ),
          [stopLoading({ key: "resetToRoot" })]
        );
      }

      // For other pages (drawings, etc), just reset breadcrumb
      return of(navigationActions.resetToRootSuccess());
    })
  );

/**
 * Epic for loading workspace data
 * Called when user selects a workspace from sidebar
 */
export const loadWorkspaceEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(navigationActions.loadWorkspaceRequest.match),
    switchMap((action) => {
      const { workspace, page = 1, pageSize = 20, folderRootId } = action.payload;

      // Determine which API to call based on workspace
      let loadDataApi;
      const rootFolderId = folderRootId;

      if (workspace === "My Drive") {
        // Load My Drive root folder contents
        loadDataApi = from([{ data: { results: [], page: 1, pageCount: 0, pageSize, queryCount: 0 } }]);
      } else if (workspace === "Shared with me") {
        // For Shared with me, use drawings API
        // This should be imported from drawings actions or similar
        loadDataApi = from([{ data: { results: [], page: 1, pageCount: 0, pageSize, queryCount: 0 } }]);
      } else if (workspace === "Team Drives") {
        // Load team drives root
        loadDataApi = from([{ data: { results: [], page: 1, pageCount: 0, pageSize, queryCount: 0 } }]);
      } else if (workspace === "Trash") {
        loadDataApi = from([{ data: { results: [], page: 1, pageCount: 0, pageSize, queryCount: 0 } }]);
      } else {
        return of(
          navigationActions.loadWorkspaceFailure("Unknown workspace type")
        );
      }

      return concat(
        [startLoading({ key: "loadWorkspace" })],
        loadDataApi.pipe(
          switchMap((res: any) => {
            localStorage.setItem("folderId", rootFolderId);

            const projectFiles = (res.data.results || []);

            return from([
              navigationActions.loadWorkspaceSuccess({
                workspace,
                rootFolderId,
              }),
            ]);
          }),
          catchError((err) => {
            console.error("Error loading workspace:", err);
            return of(
              navigationActions.loadWorkspaceFailure(
                err.response?.Message || "Error loading workspace"
              )
            );
          })
        ),
        [stopLoading({ key: "loadWorkspace" })]
      );
    })
  );

/**
 * Epic for resolving folder context from link sharing
 * This is the SOURCE OF TRUTH for workspace, breadcrumb, and permissions
 */
export const resolveFromLinkEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(navigationActions.resolveFromLinkRequest.match),
    switchMap((action) => {
      const { folderId, page = 1, pageSize = 20 } = action.payload;

      return concat(
        [startLoading({ key: "resolveFromLink" })],
        FolderService.Get.getFolderContext(folderId, 0).pipe(
          switchMap((context) => {
            // Store folder ID in localStorage
            localStorage.setItem("folderId", folderId);

            // Transform API path to breadcrumb items
            const breadcrumbPath = context.path.map((item, index) => ({
              id: item.id,
              name: item.name,
              isRoot: index === 0, // First item is root
            }));

            // Determine which API to call based on workspace
            const loadItemsApi =
              context.workspace === "Team Drives"
                ? FolderService.Get.getTeamFilesByFolderId(folderId, {
                    page,
                    pageSize,
                    isOrganization: true,
                  })
                : FolderService.Get.getFilesByFolderId(folderId, {
                    page,
                    pageSize,
                  });

            return loadItemsApi.pipe(
              switchMap((res) => {
                const projectFiles = (res.data.results || []);

                return from([
                  navigationActions.resolveFromLinkSuccess({
                    folderId: context.id,
                    folderName: context.name,
                    workspace: context.workspace,
                    breadcrumbPath,
                    ownerRootId: context.ownerRootId,
                  }),
                  fileActions.setParentFiles(projectFiles),
                  fileActions.setPagination({
                    page: res.data.page,
                    pageCount: res.data.pageCount,
                    pageSize: res.data.pageSize,
                    queryCount: res.data.queryCount,
                    firstRowIndex: (res.data.page - 1) * res.data.pageSize + 1,
                    lastRowIndex: Math.min(
                      res.data.page * res.data.pageSize,
                      res.data.queryCount
                    ),
                  }),
                ]);
              })
            );
          }),
          catchError((err) => {
            console.error("Error resolving from link:", err);
            message.error(handleHttpError(err));
            return of(
              navigationActions.resolveFromLinkFailure(
                err.response?.Message || "Error resolving folder link"
              )
            );
          })
        ),
        [stopLoading({ key: "resolveFromLink" })]
      );
    })
  );

/**
 * Epic for resolving file context from link sharing
 * This is the SOURCE OF TRUTH for workspace, breadcrumb, and permissions when sharing a file
 */
export const resolveFromFileLinkEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(navigationActions.resolveFromFileLinkRequest.match),
    switchMap((action) => {
      const { fileId } = action.payload;

      return concat(
        of(startLoading({ key: "resolveFromFileLink" })),

        FolderService.Get.getFileAccessContext(fileId).pipe(
          switchMap((context) => {
            const parentFolderId = context.data.parentId;

            // ✅ side-effect
            localStorage.setItem("folderId", parentFolderId);

            const breadcrumbPath = context.path.map((item, index) => ({
              id: item.id,
              name: item.name,
              isRoot: index === 0,
            }));

            const files = context.data ? [context.data].map(mapApiToFolderRecord) : [];

            return from([
              navigationActions.resolveFromFileLinkSuccess({
                parentFolderId,
                folderName:
                  context.path[context.path.length - 1]?.name ?? "Files",
                workspace: context.workspace,
                breadcrumbPath,
                ownerRootId: context.ownerRootId,
              }),

              navigationActions.setDataSingleFile(files),

              fileActions.setPagination({
                page: 1,
                pageSize: 20,
                pageCount: 1,
                queryCount: 1,
              }),
            ]);
          }),

          catchError((err) => {
            console.error("Error resolving from file link:", err);
            message.error(handleHttpError(err));

            return of(
              navigationActions.resolveFromFileLinkFailure(
                err.response?.Message || "Error resolving file link"
              )
            );
          })
        ),

        of(stopLoading({ key: "resolveFromFileLink" }))
      );
    })
  );

  export const clearSingleFileOnNavigateEpic: Epic<Action, Action> = (
  action$
) =>
  action$.pipe(
    ofType(
      navigationActions.navigateToFolderRequest.type,
      navigationActions.navigateToBreadcrumbRequest.type,
      navigationActions.resolveFromLinkRequest.type,
      navigationActions.resetToRootRequest.type,
      navigationActions.loadWorkspaceRequest.type,
      fileActions.searchFilesRequest.type
    ),
    map(() => {
      return navigationActions.setDataSingleFile([]);
    })
  );

/**
 * Epic for syncing folderRootId to localStorage when reset to root succeeds
 */
export const resetToRootSideEffectEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(navigationActions.resetToRootSuccess.match),
    map(() => {
      const state = state$.value;
      const { folderRootId } = state.navigation;
      localStorage.setItem("folderId", folderRootId);
      return { type: "@@NOOP" } as Action;
    })
  );

/**
 * Epic for syncing currentFolderId to localStorage when breadcrumb is removed (folder deleted)
 */
export const removeBreadcrumbSideEffectEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(navigationActions.removeBreadcrumb.match),
    map(() => {
      const state = state$.value;
      const { currentFolderId } = state.navigation;
      // Update localStorage to new current folder
      if (currentFolderId) {
        localStorage.setItem("folderId", currentFolderId);

      }
      return { type: "@@NOOP" } as Action;
    })
  );

export const navigationEpics = [
  navigateToFolderEpic$,
  navigateToBreadcrumbEpic$,
  resetToRootEpic$,
  loadWorkspaceEpic$,
  resolveFromLinkEpic$,
  resolveFromFileLinkEpic$,
  clearSingleFileOnNavigateEpic,
  resetToRootSideEffectEpic$,
  removeBreadcrumbSideEffectEpic$,
];

export const navigationActions = navigationSlice.actions;
export const navigationReducer = navigationSlice.reducer;

// Selectors
export const selectCurrentFolderId = (state: any) =>
  state.navigation.currentFolderId;
export const selectBreadcrumbs = (state: any) => state.navigation.breadcrumbs;
export const selectFolderRootId = (state: any) => state.navigation.folderRootId;
export const selectNavigationLoading = (state: any) => state.navigation.loading;
export const selectCurrentWorkspace = (state: any) => state.navigation.currentWorkspace;
export const selectOwnerRootId = (state: any) => state.navigation.ownerRootId;
